import {
  type BoundFormatOptions,
  type BoundSide,
  boundParts,
  type FieldUnitSpec,
  type MeasureFormatOptions,
  type MeasureParts,
  measureParts,
  resolveUnitId,
  type SiValue,
  type UnitFormatOptions,
  formatBound as unitsFormatBound,
  formatMeasure as unitsFormatMeasure,
  formatNumberWithUnit as unitsFormatNumberWithUnit,
  formatTolerance as unitsFormatTolerance,
} from "lab-testing-units";

/** The single resolution every displayed number shares. `navigator.language` is not the host default. */
export const displayLocale = (locale?: string): string | undefined => locale ?? (navigator.language || undefined);

export const formatNumber = (value: number | null | undefined, decimals = 2, locale?: string): string => {
  if (value == null) return "-";
  const normalized = Object.is(value, -0) ? 0 : value;
  return normalized.toLocaleString(displayLocale(locale), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatNumberUpTo = (value: number, maxDecimals: number, locale?: string): string =>
  (Object.is(value, -0) ? 0 : value).toLocaleString(displayLocale(locale), {
    maximumFractionDigits: maxDecimals,
    useGrouping: false,
  });

export const decimalsUpTo = (value: number, minDecimals: number, maxDecimals: number): number => {
  const scale = 10 ** maxDecimals;
  return Math.max(minDecimals, (String(Math.round(value * scale) / scale).split(".")[1] ?? "").length);
};

/**
 * The number a `formatNumber` cell shows, as a number — for logic that must agree with what the user
 * reads. Rounds through `Intl` for that reason: `Math.round(v * 10)` disagrees near ties (7.6499…995
 * displays as 7.6 but multiply-rounds to 7.7), and `toFixed` disagrees more often still.
 */
export const roundToDisplay = (value: number | null | undefined, decimals: number): number | null => {
  if (value == null) return null;
  const rounded = Number(
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: false,
    }).format(value),
  );
  return Object.is(rounded, -0) ? 0 : rounded;
};

/** Rounded before formatting, or a near-zero negative reads "-0.00". */
export const formatMeasureParts = ({ value, dp }: MeasureParts, locale?: string): string =>
  formatNumber(roundToDisplay(value, dp), dp, locale);

/**
 * A measure as a bare number, converted and formatted but carrying no symbol — the cell of a table whose
 * unit is named elsewhere, in a column header or a row's label. Where the symbol belongs to the value
 * itself, `formatMeasure` prints both and `UnitValue` renders them as separate nodes.
 *
 * `resolveUnitId` because a stored id outlives the list that offered it and `findUnit` throws on one the
 * registry has retired — these routes carry no ErrorBoundary.
 */
export const formatMeasureValue = (
  value: SiValue | number | null | undefined,
  spec: FieldUnitSpec,
  unitId?: string,
  dp?: number | Readonly<Record<string, number>>,
  locale?: string,
): string => formatMeasureParts(measureParts(value, spec, resolveUnitId(spec, unitId), { dp }), locale);

export const formatBoundValue = (
  value: number,
  spec: FieldUnitSpec,
  unitId: string | undefined,
  side: BoundSide,
  dp?: number | Readonly<Record<string, number>>,
  locale?: string,
): string => formatMeasureParts(boundParts(value, spec, resolveUnitId(spec, unitId), { dp, side }), locale);

export const unitSymbol = (spec: FieldUnitSpec, unitId?: string): string =>
  measureParts(null, spec, resolveUnitId(spec, unitId)).symbol;

/**
 * The `lab-testing-units` formatters on the app's locale. Import these, never the package's own — those
 * fall to the runtime default (OL-800 D7 keeps `units/` free of globals), which is not `navigator.language`,
 * so a measure would take a separator no other number on the page uses.
 */
export const formatMeasure = (
  value: SiValue | number | null | undefined,
  spec: FieldUnitSpec,
  unitId?: string,
  options: MeasureFormatOptions = {},
): string => unitsFormatMeasure(value, spec, unitId, { ...options, locale: displayLocale(options.locale) });

export const formatBound = (
  value: number,
  spec: FieldUnitSpec,
  unitId: string | undefined,
  options: BoundFormatOptions,
): string =>
  unitsFormatBound(value, spec, resolveUnitId(spec, unitId), { ...options, locale: displayLocale(options.locale) });

export const formatTolerance = (
  canonicalDelta: number,
  spec: FieldUnitSpec,
  unitId?: string,
  options: MeasureFormatOptions = {},
): string => unitsFormatTolerance(canonicalDelta, spec, unitId, { ...options, locale: displayLocale(options.locale) });

export const formatNumberWithUnit = (
  value: number | null | undefined,
  symbol: string,
  dp: number,
  options: UnitFormatOptions = {},
): string => unitsFormatNumberWithUnit(value, symbol, dp, { ...options, locale: displayLocale(options.locale) });

export const formatAstmSieveLabel = (label: string): string => (/no/i.test(label) ? label : `${label} in`);

export const formatParticleSize = (value: number | null | undefined, locale?: string): string => {
  if (value == null) return "-";

  if (value >= 5) return formatNumber(value, 1, locale);
  if (value >= 0.5) return formatNumber(value, 2, locale);
  if (value >= 0.05) return formatNumber(value, 3, locale);
  return formatNumber(value, 4, locale);
};

const elapsedDp = (value: number): number => (Number.isInteger(value) ? 0 : 1);

export const formatElapsedTime = (minutes: number | null | undefined, locale?: string): string => {
  if (minutes == null || !Number.isFinite(minutes)) return "-";

  if (minutes < 1) {
    const seconds = minutes * 60;
    return formatNumberWithUnit(seconds, "s", elapsedDp(seconds), { locale });
  }
  if (minutes < 60) return formatNumberWithUnit(minutes, "min", elapsedDp(minutes), { locale });

  const hours = minutes / 60;
  return formatNumberWithUnit(hours, "h", elapsedDp(hours), { locale });
};

export const formatWaterContent = (value: number | null | undefined, locale?: string): string => {
  if (value == null) return "-";
  if (value >= 99.95) return formatNumber(value, 0, locale);
  return formatNumber(value, 1, locale);
};

export const getDecimalSeparator = (locale?: string): string => {
  const parts = new Intl.NumberFormat(displayLocale(locale)).formatToParts(1.1);
  return parts.find((p) => p.type === "decimal")?.value ?? ".";
};

export const parseLocaleNumber = (str: string, locale?: string): number | null => {
  // Drop whitespace grouping separators (space / NBSP / narrow NBSP, e.g. fr-FR "1 234,56") so pasted
  // grouped values parse. Symbolic grouping (".", "," etc.) stays rejected — it's ambiguous with the decimal.
  const cleaned = str.replace(/\s/g, "");
  if (!cleaned || cleaned === "-") return null;
  const sep = getDecimalSeparator(locale);
  const escapedSep = sep.replace(".", "\\.");
  const pattern = new RegExp(`^-?\\d*(${escapedSep}\\d*)?$`);
  if (!pattern.test(cleaned)) return null;
  const normalized = sep !== "." ? cleaned.replace(sep, ".") : cleaned;
  const result = parseFloat(normalized);
  return Number.isNaN(result) ? null : result;
};
